import * as React from 'react';
import { useRecordContext, useTranslate } from 'react-admin';
import { Chip, Typography } from '@mui/material';
import { StatsTooltip } from '../../components/statistics/common';

interface ValidityStatusBadgeProps {
    record?: any;
    statusSource?: string;
    statusResourceKey?: string;
    label?: string;
    showAsText?: boolean;
    variant?: 'chip' | 'caption';
    // Backward compatibility (optional / legacy usage)
    resource?: string;
    statusUrl?: string;
    validFromSource?: string;
    validToSource?: string;
}

const ValidityStatusBadge: React.FC<ValidityStatusBadgeProps> = ({
    record,
    statusSource = 'status',
    statusResourceKey = 'resources.teacher.status',
    label,
    showAsText = false,
    variant = 'chip',
    // backward compat props (not otherwise used)
    resource,
    statusUrl,
    validFromSource = 'public.validfrom',
    validToSource = 'public.validto',
}) => {
    const contextRecord = useRecordContext();
    const actualRecord = record || contextRecord;
    const translate = useTranslate();

    // No record: show simple placeholder (no form elements)
    if (!actualRecord) {
        const statusLabel =
            label || translate('resources.user.fields.group.status');
        if (showAsText) return <Typography variant="body2">{statusLabel}</Typography>;
        return variant === 'caption' ? (
            <Typography
                variant="caption"
                sx={{
                    backgroundColor: '#9e9e9e',
                    color: 'white',
                    padding: '0 4px',
                    borderRadius: '4px',
                    marginLeft: '8px',
                }}
            >
                {statusLabel}
            </Typography>
        ) : (
            <Chip size="small" label={statusLabel} variant="filled" />
        );
    }

    // Helper to access nested props via dot path
    const getByPath = (obj: any, path?: string) =>
        !obj || !path
            ? undefined
            : path.split('.').reduce(
                  (acc, key) => (acc == null ? undefined : acc[key]),
                  obj
              );

    const validFromRaw = getByPath(actualRecord, validFromSource);
    const validToRaw = getByPath(actualRecord, validToSource);

    const validFrom = validFromRaw ? new Date(validFromRaw) : null;
    const validTo = validToRaw ? new Date(validToRaw) : null;
    const currentDate = new Date();
    const statusValue = actualRecord[statusSource];

    let isValidByDate = false;
    let showBadge = false;

    const hasValidityDates = Boolean(validFrom || validTo);
    const isInactiveStatus = statusValue !== 0 && statusValue !== '0';

    if (hasValidityDates || isInactiveStatus) {
        showBadge = true;
        if (hasValidityDates) {
            if (validFrom && validTo) {
                isValidByDate = currentDate >= validFrom && currentDate <= validTo;
            } else if (validFrom) {
                isValidByDate = currentDate >= validFrom;
            } else if (validTo) {
                isValidByDate = currentDate <= validTo;
            }
        } else {
            isValidByDate = true;
        }
    }

    const isValid = isValidByDate && !isInactiveStatus;

    const formatDate = (date: Date) =>
        date.toLocaleDateString('fi-FI', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
        });

    const statusText = React.useMemo(() => {
        const translationKey = `${statusResourceKey}.${statusValue}`;
        const translatedStatus = translate(translationKey);
        if (translatedStatus === translationKey) {
            return statusValue?.toString() || 'Status';
        }
        return translatedStatus.split(' ||||')[0] || translatedStatus;
    }, [statusValue, translate, statusResourceKey]);

    // Add asterisk if record has validity dates
    const displayStatusText = React.useMemo(
        () => (hasValidityDates ? `${statusText}*` : statusText),
        [statusText, hasValidityDates]
    );

    const tooltipContent = showBadge ? (
        <div>
            <div
                style={{
                    fontSize: '16px',
                    fontWeight: 'bold',
                    marginBottom: '8px',
                    paddingBottom: '4px',
                }}
            >
                {translate('resources.teacher.fields.group.valid')}
            </div>
            {validFrom && (
                <div>
                    {translate('resources.teacher.fields.group.validfrom')}:{' '}
                    {formatDate(validFrom)}
                </div>
            )}
            {validTo && (
                <div>
                    {translate('resources.teacher.fields.group.validto')}:{' '}
                    {formatDate(validTo)}
                </div>
            )}
        </div>
    ) : null;

    if (showBadge) {
        const badgeElement =
            variant === 'caption' ? (
                <Typography
                    variant="caption"
                    sx={{
                        backgroundColor: isValid ? '#4caf50' : '#f44336',
                        color: 'white',
                        padding: '0 4px',
                        borderRadius: '4px',
                        marginLeft: '8px',
                    }}
                >
                    {displayStatusText}
                </Typography>
            ) : (
                <Chip
                    size="small"
                    label={displayStatusText}
                    color={isValid ? 'success' : 'error'}
                    variant="filled"
                />
            );

        if (hasValidityDates) {
            return (
                <StatsTooltip
                    title={tooltipContent}
                    followCursor
                    placement="top-start"
                    componentsProps={{
                        tooltip: {
                            sx: {
                                padding: '12px 16px',
                                fontSize: '14px',
                                lineHeight: 1.4,
                            },
                        },
                    }}
                >
                    {badgeElement}
                </StatsTooltip>
            );
        }
        return badgeElement;
    }

    // No badge conditions met: show plain status text/badge (read-only)
    if (showAsText) return <Typography variant="body2">{displayStatusText}</Typography>;
    return variant === 'caption' ? (
        <Typography
            variant="caption"
            sx={{
                backgroundColor: '#9e9e9e',
                color: 'white',
                padding: '0 4px',
                borderRadius: '4px',
                marginLeft: '8px',
            }}
        >
            {displayStatusText}
        </Typography>
    ) : (
        <Chip size="small" label={displayStatusText} variant="filled" />
    );
};

export default ValidityStatusBadge;
